import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import Paywall from '../components/Paywall';
import { toast } from 'sonner';
import { 
    Brain, Sparkles, Heart, TrendingUp,
    Loader2, Calendar, Zap, RefreshCw, Award, BarChart3, Lock
} from 'lucide-react';
import {
    LineChart, Line, AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const InsightsPage = () => {
    const { user, api, checkFeature } = useAuth();
    const [weeklySummary, setWeeklySummary] = useState(null);
    const [deeperInsights, setDeeperInsights] = useState(null);
    const [affirmation, setAffirmation] = useState(null);
    const [moodChartData, setMoodChartData] = useState([]);
    const [loading, setLoading] = useState({ weekly: true, deeper: false, affirmation: false, chart: true });
    const [showPaywall, setShowPaywall] = useState({ show: false, type: 'plus', feature: '' });
    const [affirmationStyle, setAffirmationStyle] = useState(user?.affirmation_style || 'general');

    const subscription = user?.subscription || { tier: 'basic', addons: [] };
    const hasDeepInsights = subscription.tier === 'complete' || subscription.addons?.includes('deeper_insights');
    const hasCharts = subscription.tier === 'plus' || subscription.tier === 'complete';

    useEffect(() => {
        fetchWeeklySummary();
        fetchAffirmation();
        fetchMoodChartData();
    }, []);

    const fetchWeeklySummary = async () => {
        try {
            const response = await api.get('/journal/weekly-summary');
            setWeeklySummary(response.data);
        } catch (error) {
            console.error('Failed to fetch weekly summary:', error);
        } finally {
            setLoading(prev => ({ ...prev, weekly: false }));
        }
    };

    const fetchAffirmation = async () => {
        setLoading(prev => ({ ...prev, affirmation: true }));
        try {
            const response = await api.get('/ai/affirmation');
            setAffirmation(response.data);
        } catch (error) {
            console.error('Failed to fetch affirmation:', error);
        } finally {
            setLoading(prev => ({ ...prev, affirmation: false }));
        }
    };

    const fetchMoodChartData = async () => {
        try {
            const response = await api.get('/journal/stats?days=30');
            const entries = response.data.entries || [];
            
            // Process entries for chart
            const chartData = entries.slice().reverse().map(entry => ({
                date: new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                mood: entry.mood_rating,
                emoji: entry.emoji
            }));
            
            setMoodChartData(chartData);
        } catch (error) {
            console.error('Failed to fetch chart data:', error);
        } finally {
            setLoading(prev => ({ ...prev, chart: false }));
        }
    };

    const handleAffirmationStyleChange = async (isSpiritual) => {
        const newStyle = isSpiritual ? 'spiritual' : 'general';
        setAffirmationStyle(newStyle);
        try {
            await api.put('/auth/affirmation-style', { style: newStyle });
            toast.success(`Affirmation style updated to ${newStyle}`);
            fetchAffirmation();
        } catch (error) {
            toast.error('Failed to update preference');
        }
    };

    const fetchDeeperInsights = async () => {
        if (!hasDeepInsights) {
            setShowPaywall({ show: true, type: 'deeper_insights', feature: 'Deeper Insights' });
            return;
        }

        setLoading(prev => ({ ...prev, deeper: true }));
        try {
            const response = await api.post('/ai/deeper-insights');
            setDeeperInsights(response.data);
        } catch (error) {
            if (error.response?.status === 403) {
                setShowPaywall({ show: true, type: 'deeper_insights', feature: 'Deeper Insights' });
            } else {
                toast.error(error.response?.data?.detail || 'Failed to generate insights');
            }
        } finally {
            setLoading(prev => ({ ...prev, deeper: false }));
        }
    };

    const handleUpgrade = (type) => {
        setShowPaywall({ show: false, type: '', feature: '' });
        window.location.href = '/pricing';
    };

    const getMoodTrendIcon = (trend) => {
        switch (trend) {
            case 'improving': return <TrendingUp className="w-5 h-5 text-chart-1" />;
            case 'declining': return <TrendingUp className="w-5 h-5 text-destructive rotate-180" />;
            default: return <TrendingUp className="w-5 h-5 text-muted-foreground" />;
        }
    };

    const badges = weeklySummary?.badges || user?.badges || [];
    const nextBadge = weeklySummary?.next_badge;

    // Custom tooltip for chart
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-lg">
                        <span className="text-2xl mr-2">{payload[0].payload.emoji}</span>
                        Mood: {payload[0].value}/10
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <DashboardLayout>
            <div data-testid="insights-page" className="max-w-4xl mx-auto space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <h1 className="font-heading text-3xl font-semibold text-foreground">
                        Your Insights
                    </h1>
                    {user?.mood_streak > 0 && (
                        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-chart-4/20">
                            <Zap className="w-5 h-5 text-chart-4" />
                            <span className="font-medium">{user.mood_streak} day streak!</span>
                        </div>
                    )}
                </div>

                {/* Mood Analytics Chart */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <Card className="glass-card">
                        <CardHeader>
                            <CardTitle className="font-heading text-xl flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-chart-2" />
                                Mood Analytics
                                {!hasCharts && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary flex items-center gap-1">
                                        <Lock className="w-3 h-3" /> Plus
                                    </span>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {!hasCharts ? (
                                <div className="text-center py-8">
                                    <div className="relative">
                                        {/* Blurred preview */}
                                        <div className="h-48 bg-gradient-to-r from-chart-1/10 via-chart-2/10 to-chart-3/10 rounded-xl blur-sm" />
                                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                                            <Lock className="w-8 h-8 text-muted-foreground mb-2" />
                                            <p className="text-muted-foreground mb-4">
                                                Visualize your mood trends over time
                                            </p>
                                            <Button
                                                onClick={() => setShowPaywall({ show: true, type: 'plus', feature: 'Mood Analytics Charts' })}
                                                className="rounded-full"
                                            >
                                                <Sparkles className="w-4 h-4 mr-2" />
                                                Unlock with Plus
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ) : loading.chart ? (
                                <div className="flex items-center justify-center h-48">
                                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : moodChartData.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>Start journaling to see your mood trends!</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Line Chart */}
                                    <div>
                                        <h4 className="text-sm font-medium text-muted-foreground mb-4">Mood Over Time</h4>
                                        <ResponsiveContainer width="100%" height={200}>
                                            <AreaChart data={moodChartData}>
                                                <defs>
                                                    <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3}/>
                                                        <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                                <XAxis 
                                                    dataKey="date" 
                                                    tick={{ fontSize: 12 }}
                                                    stroke="hsl(var(--muted-foreground))"
                                                />
                                                <YAxis 
                                                    domain={[1, 10]} 
                                                    tick={{ fontSize: 12 }}
                                                    stroke="hsl(var(--muted-foreground))"
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Area
                                                    type="monotone"
                                                    dataKey="mood"
                                                    stroke="hsl(var(--chart-1))"
                                                    fill="url(#moodGradient)"
                                                    strokeWidth={2}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Bar Chart */}
                                    <div>
                                        <h4 className="text-sm font-medium text-muted-foreground mb-4">Daily Mood Comparison</h4>
                                        <ResponsiveContainer width="100%" height={150}>
                                            <BarChart data={moodChartData.slice(-7)}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                                <XAxis 
                                                    dataKey="date" 
                                                    tick={{ fontSize: 12 }}
                                                    stroke="hsl(var(--muted-foreground))"
                                                />
                                                <YAxis 
                                                    domain={[1, 10]} 
                                                    tick={{ fontSize: 12 }}
                                                    stroke="hsl(var(--muted-foreground))"
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Bar 
                                                    dataKey="mood" 
                                                    fill="hsl(var(--chart-2))" 
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Streak Badges */}
                {badges.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <Card className="glass-card">
                            <CardHeader>
                                <CardTitle className="font-heading text-xl flex items-center gap-2">
                                    <Award className="w-5 h-5 text-chart-4" />
                                    Your Badges
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-wrap gap-4">
                                    {badges.map((badge, index) => (
                                        <div 
                                            key={badge.name}
                                            className="flex flex-col items-center gap-2 p-3 rounded-xl bg-chart-4/10"
                                        >
                                            <div className="w-12 h-12 bg-chart-4/20 rounded-full flex items-center justify-center">
                                                <span className="text-2xl">
                                                    {badge.icon === 'seedling' && '🌱'}
                                                    {badge.icon === 'flame' && '🔥'}
                                                    {badge.icon === 'star' && '⭐'}
                                                    {badge.icon === 'trophy' && '🏆'}
                                                    {badge.icon === 'medal' && '🏅'}
                                                    {badge.icon === 'crown' && '👑'}
                                                    {badge.icon === 'gem' && '💎'}
                                                    {badge.icon === 'sparkles' && '✨'}
                                                </span>
                                            </div>
                                            <span className="text-sm font-medium text-center">{badge.name}</span>
                                            <span className="text-xs text-muted-foreground">{badge.description}</span>
                                        </div>
                                    ))}
                                </div>
                                {nextBadge && (
                                    <div className="mt-4 p-3 rounded-xl bg-muted/30">
                                        <p className="text-sm text-muted-foreground">
                                            <span className="font-medium text-foreground">{nextBadge.days_remaining} more days</span> until your next badge: {nextBadge.badge.name}
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Daily Affirmation */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <Card className="glass-card bg-gradient-to-br from-primary/5 to-accent/5">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between flex-wrap gap-4">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                                        <Heart className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium">Today&apos;s Affirmation</h3>
                                        {affirmation?.personalized && (
                                            <span className="text-xs text-primary">Personalized for you</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <Label htmlFor="spiritual" className="text-sm text-muted-foreground">
                                            Spiritual/Biblical
                                        </Label>
                                        <Switch
                                            id="spiritual"
                                            checked={affirmationStyle === 'spiritual'}
                                            onCheckedChange={handleAffirmationStyleChange}
                                            data-testid="affirmation-style-toggle"
                                        />
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={fetchAffirmation}
                                        disabled={loading.affirmation}
                                    >
                                        <RefreshCw className={`w-4 h-4 ${loading.affirmation ? 'animate-spin' : ''}`} />
                                    </Button>
                                </div>
                            </div>
                            <p className="text-lg font-heading italic text-foreground">
                                &quot;{affirmation?.affirmation || 'Loading...'}&quot;
                            </p>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Weekly Summary */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <Card className="glass-card">
                        <CardHeader>
                            <CardTitle className="font-heading text-xl flex items-center gap-2">
                                <Calendar className="w-5 h-5" />
                                Weekly Mood Summary
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {loading.weekly ? (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Loading summary...
                                </div>
                            ) : weeklySummary ? (
                                <div className="space-y-4">
                                    {/* Stats Row */}
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="text-center p-4 rounded-xl bg-muted/30">
                                            <p className="text-2xl font-heading font-bold">
                                                {weeklySummary.entries_count}
                                            </p>
                                            <p className="text-sm text-muted-foreground">Entries</p>
                                        </div>
                                        <div className="text-center p-4 rounded-xl bg-muted/30">
                                            <p className="text-2xl font-heading font-bold">
                                                {weeklySummary.average_mood}/10
                                            </p>
                                            <p className="text-sm text-muted-foreground">Avg Mood</p>
                                        </div>
                                        <div className="text-center p-4 rounded-xl bg-muted/30">
                                            <div className="flex items-center justify-center gap-2">
                                                {getMoodTrendIcon(weeklySummary.mood_trend)}
                                                <p className="text-lg font-medium capitalize">
                                                    {weeklySummary.mood_trend}
                                                </p>
                                            </div>
                                            <p className="text-sm text-muted-foreground">Trend</p>
                                        </div>
                                    </div>

                                    {/* AI Summary */}
                                    <div className="p-4 rounded-xl bg-accent/10">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Sparkles className="w-4 h-4 text-chart-3" />
                                            <span className="text-sm font-medium">
                                                {weeklySummary.is_advanced ? 'AI Analysis' : 'Summary'}
                                            </span>
                                        </div>
                                        <p className="text-foreground whitespace-pre-wrap">
                                            {weeklySummary.summary}
                                        </p>
                                    </div>

                                    {!weeklySummary.is_advanced && (
                                        <Button
                                            variant="outline"
                                            className="w-full rounded-full"
                                            onClick={() => setShowPaywall({ show: true, type: 'plus', feature: 'AI Weekly Analysis' })}
                                        >
                                            <Sparkles className="w-4 h-4 mr-2" />
                                            Get AI-powered insights with Plus
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <p className="text-muted-foreground">No data available yet</p>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Deeper Insights Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    <Card className="glass-card">
                        <CardHeader>
                            <CardTitle className="font-heading text-xl flex items-center gap-2">
                                <Brain className="w-5 h-5 text-chart-3" />
                                Deeper Insights
                                {!hasDeepInsights && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                                        Addon
                                    </span>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {deeperInsights ? (
                                <div className="space-y-4">
                                    <p className="text-sm text-muted-foreground">
                                        Based on {deeperInsights.entries_analyzed} entries • {deeperInsights.period}
                                    </p>
                                    <div className="prose prose-sm dark:prose-invert max-w-none">
                                        <div className="whitespace-pre-wrap text-foreground">
                                            {deeperInsights.insights}
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        onClick={fetchDeeperInsights}
                                        disabled={loading.deeper}
                                        className="rounded-full"
                                    >
                                        <RefreshCw className={`w-4 h-4 mr-2 ${loading.deeper ? 'animate-spin' : ''}`} />
                                        Refresh Insights
                                    </Button>
                                </div>
                            ) : (
                                <div className="text-center py-6">
                                    <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                    <p className="text-muted-foreground mb-4">
                                        Get AI-powered analysis of your emotional patterns, triggers, 
                                        burnout signals, and personalized coping strategies.
                                    </p>
                                    <Button
                                        onClick={fetchDeeperInsights}
                                        disabled={loading.deeper}
                                        className="rounded-full"
                                        data-testid="get-deeper-insights"
                                    >
                                        {loading.deeper ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Analyzing...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles className="w-4 h-4 mr-2" />
                                                {hasDeepInsights ? 'Generate Insights' : 'Unlock Deeper Insights'}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>
            </div>

            {/* Paywall Modal */}
            <Paywall
                type={showPaywall.type}
                isOpen={showPaywall.show}
                onClose={() => setShowPaywall({ show: false, type: '', feature: '' })}
                onUpgrade={handleUpgrade}
                feature={showPaywall.feature}
            />
        </DashboardLayout>
    );
};

export default InsightsPage;
